import { getEnv } from "@pathwright/utils/src/env/env"
import { setMomentLocale } from "@pathwright/utils/src/moment/setMomentLocale"
import i18next, { InitOptions, Module, TFunction } from "i18next"
import { transformNamespace } from "i18next-v4-format-converter"
import defaultsDeep from "lodash/defaultsDeep"
import { ReactNode, useEffect, useState } from "react"
import { I18nextProvider } from "react-i18next"
import { I18_DEFAULT_ENGLISH_SPACE, I18_DEFAULT_NAME_SPACE } from "./constants"
import { Translation } from "./types"
import {
  addTranslationFormatters,
  applyDeprecatedTranslations,
  getLocalTranslation,
  preProcessTranslation
} from "./utils"

export const i18nVisibleKey = "display_i18n_keys"
export const i18nTogglerActiveKey = "i18n-keys-active"

let translationEditMode = false
const getIsTranlsationEditMode = () => {
  try {
    const storedValue = window.localStorage.getItem(i18nVisibleKey)
    translationEditMode = storedValue ? eval(JSON.parse(storedValue)) : false
  } catch (error) {
    console.log("error loading i18nVisibleKey ", error)
  }
  return translationEditMode
}

interface PostProcessorModule extends Module {
  type: "postProcessor"
  name: string
  process: (
    value: string,
    key: string,
    options: unknown,
    translator: TFunction
  ) => string
}

// Currently only handles the translation output for when
// in translationEditMode.
const postProcessor: PostProcessorModule = {
  type: "postProcessor",
  name: "postProcessor",
  process: function (
    value: string,
    key: string,
    options: unknown,
    translator: TFunction
  ) {
    if (getIsTranlsationEditMode()) {
      const translationKey = key || value
      return `<${translationKey}>`
    }
    return value
  }
}

// This literally seems to export the i18next instance.
// Importing i18next directly in other components will run into
// issues if you need i18next instance features (like `addResources`).
// Haven't had any luck getting the i18next instance from react-i18next
// context either.
export const i18n = i18next.use(postProcessor as unknown as Module)

export interface ProviderProps {
  children: ReactNode
  localI18nBackendPlugin?: unknown
  translation?: Translation
  lng?: string
  useLocalTranslation?: boolean
  debug?: boolean
}

const Provider = ({
  children,
  localI18nBackendPlugin,
  translation: propsTranslation,
  lng = "en",
  useLocalTranslation,
  debug
}: ProviderProps) => {
  // Reset to english if using local translation.
  if (useLocalTranslation) {
    lng = "en"
  }
  // Note that i18next has an isInitialized key that gets set to true after
  // i18next.init completes, but it does not trigger a re-render, hence using state.
  const [initialized, setInitialized] = useState(false)
  const [currentTranslation, setCurrentTranslation] = useState<Translation>()

  // See notes regarding change in plural keys: https://www.i18next.com/translation-function/plurals
  function getTransformedTranslation(translation: Translation) {
    // Transform legacy "_plural" keys to "_other".
    const transformedTranslation = transformNamespace(
      translation.language,
      translation.translation
    )

    // Now merge back in our non-transformed translation in order to
    // retain the "_plural" keys which are used in nested translations
    // (i.e. "New t('path.label_plural')").
    const mergedTranslation = defaultsDeep(
      transformedTranslation,
      translation.translation
    )

    // Now, map any deprecated keys to the new keys.
    applyDeprecatedTranslations(mergedTranslation)

    // Overwrite the translation's translation keys with the transformed translation.
    return {
      ...translation,
      translation: mergedTranslation
    }
  }

  function handleSetCurrentTranslation(translation: Translation) {
    // Set transformed translation as the current translation.
    return setCurrentTranslation(getTransformedTranslation(translation))
  }

  // This triggers an "added" event on the i18next.store which will
  // in turn cause translated components to re-render.
  const updateTranslation = (translation: Translation) => {
    i18next.addResourceBundle(
      translation.language,
      I18_DEFAULT_NAME_SPACE,
      preProcessTranslation(translation.translation)
    )
  }

  const initTranslation = (translation: Translation) => {
    const i18nextOptions: InitOptions = {
      ns: [I18_DEFAULT_NAME_SPACE, I18_DEFAULT_ENGLISH_SPACE],
      defaultNS: I18_DEFAULT_NAME_SPACE,
      fallbackNS: I18_DEFAULT_ENGLISH_SPACE,
      interpolation: {
        // React already does escaping
        escapeValue: false
      },
      lng: translation.language,
      debug: debug && !getEnv("production"),
      resources: {
        [translation.language]: {
          [I18_DEFAULT_NAME_SPACE]: preProcessTranslation(
            translation.translation
          ),
          // NOTE: the translation.master_translation is null when the translation.translation
          // *is* the master translation (default english).
          [I18_DEFAULT_ENGLISH_SPACE]: translation.master_translation
            ? preProcessTranslation(
                getTransformedTranslation({
                  id: 1,
                  language: "en",
                  translation: translation.master_translation
                }).translation
              )
            : {}
        }
      },
      nsSeparator: false, // default ":" // false to allow ":" in translation key
      postProcess: "postProcessor",
      react:
        // Only binding on "added" for non-local envs to prevent re-render loop
        // when translations are auto-added via local backend.
        process.env.NODE_ENV !== "development"
          ? {
              // Necessary for translated components to re-render after updating
              // the resource bundle.
              bindI18nStore: "added"
            }
          : {},
      // Only save missing keys in local dev.
      saveMissing: process.env.NODE_ENV === "development"
    }

    // Use the local i18next backend plugin for local dev.
    if (localI18nBackendPlugin && process.env.NODE_ENV === "development") {
      i18next.use(localI18nBackendPlugin as Module)
    }

    i18n.init(i18nextOptions, () => {
      addTranslationFormatters(i18n)
      setInitialized(true)
    })
  }

  useEffect(() => {
    const handleTranslationEditMode = (
      storageEvent: CustomEvent<{ on: boolean }>
    ) => {
      translationEditMode = storageEvent.detail.on
      if (currentTranslation) {
        updateTranslation(currentTranslation)
      }
    }

    window.addEventListener(
      i18nVisibleKey,
      handleTranslationEditMode as EventListener,
      false
    )
    return () => {
      window.removeEventListener(
        i18nVisibleKey,
        handleTranslationEditMode as EventListener,
        false
      )
    }
  }, [currentTranslation])

  // Store translation prop in state.
  useEffect(() => {
    if (useLocalTranslation && process.env.NODE_ENV === "development") {
      // Only use json translation files when not on production
      // These json files are only imported locally and not included in a production build
      //
      getLocalTranslation(lng, I18_DEFAULT_NAME_SPACE).then(
        (localTranslation) => {
          if (localTranslation) handleSetCurrentTranslation(localTranslation)
        }
      )
    } else if (propsTranslation) {
      handleSetCurrentTranslation(propsTranslation)
    }
  }, [propsTranslation, lng, useLocalTranslation])

  useEffect(() => {
    if (!currentTranslation) return

    let cancelled = false

    void (async () => {
      // Load only the active Moment locale (avoids shipping all locales in the main bundle).
      await setMomentLocale(lng)
      if (cancelled) return

      if (!initialized) {
        initTranslation(currentTranslation)
      } else {
        // Update the translation keys and trigger re-render of components using translations.
        updateTranslation(currentTranslation)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [currentTranslation])

  return (
    <I18nextProvider i18n={i18next}>
      {initialized ? children : null}
    </I18nextProvider>
  )
}

Provider.displayName = "I18nextProvider.Provider"

export default Provider
